import type { CSSProperties, FC, FocusEventHandler, KeyboardEventHandler } from 'react';
import { useState } from 'react';

import type { InputBaseProps } from '@mui/material';
import { IconButton, OutlinedInput, Stack } from '@mui/material';

import CloseIcon from 'components/icons/Close';
import SearchIcon from 'components/icons/Search';

export type SearchFieldProps = {
  value: string;
  disabled?: boolean;
  placeholder?: InputBaseProps['placeholder'];
  onSearch?: () => void;
  onReset: () => void;
  onChange: InputBaseProps['onChange'];
  width?: CSSProperties['width'];
  minWidth?: CSSProperties['minWidth'];
};

export const SearchField: FC<SearchFieldProps> = ({
  disabled,
  onChange,
  onReset,
  onSearch,
  value,
  placeholder,
  width,
  minWidth
}) => {
  const [hasFocus, setHasFocus] = useState(false);

  const onBlurInput: FocusEventHandler<HTMLInputElement> = () => {
    setHasFocus(false);
  };

  const onKeyDownInput: KeyboardEventHandler<HTMLInputElement> = (event) => {
    if (event.key === 'Enter' && onSearch) {
      onSearch();
      return;
    }

    if (event.key === 'Escape') {
      onReset();
      return;
    }
  };

  const showSearchButton = onSearch || (!onSearch && value.length === 0);

  return (
    <OutlinedInput
      onChange={onChange}
      onBlur={onBlurInput}
      onKeyDown={onKeyDownInput}
      disabled={disabled}
      value={value}
      endAdornment={
        <Stack direction='row' ml={0.5}>
          <IconButton
            onClick={onReset}
            size='small'
            data-testid='clean-icon'
            sx={{ visibility: value ? 'visible' : 'hidden' }}
          >
            <CloseIcon
              fontSize='small'
              sx={(theme) => ({ color: hasFocus ? theme.palette.text.secondary : theme.palette.text.disabled })}
            />
          </IconButton>

          {showSearchButton && (
            <IconButton disabled={!onSearch} onClick={onSearch} size='small' data-testid='search-icon'>
              <SearchIcon
                fontSize='small'
                sx={(theme) => ({ color: hasFocus ? theme.palette.text.secondary : theme.palette.text.disabled })}
              />
            </IconButton>
          )}
        </Stack>
      }
      sx={(theme) => ({
        minWidth,
        width,
        height: theme.spacing(4),
        padding: theme.spacing(0.75, 1),
        backgroundColor: hasFocus ? theme.palette.common.white : theme.palette.colors.steel.A004,
        borderColor: hasFocus ? theme.palette.colors.cobalt[500] : theme.palette.grey.A200,

        '& .MuiOutlinedInput-input': {
          padding: 0
        },

        ':focus': {
          '& svg': {
            color: theme.palette.text.secondary
          }
        },

        ':hover': {
          backgroundColor: hasFocus ? theme.palette.common.white : theme.palette.colors.steel.A004,
          borderColor: hasFocus ? theme.palette.colors.cobalt[500] : theme.palette.colors.steel[50],

          '& svg': {
            color: theme.palette.text.secondary
          }
        }
      })}
      placeholder={placeholder}
    />
  );
};
